import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Input, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BreadcrumbOption } from 'ng-zorro-antd/breadcrumb';
import { filter, startWith } from 'rxjs/operators';
import { BreadcrumbService } from '../../services/breadcrumb.service';

@UntilDestroy()
@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbComponent implements OnInit {
  @Input() routeLabel: string = 'breadcrumb';
  @Input() routeLabelFn: (label: string) => string = (label) => label;
  breadcrumbs = toSignal(this.breadCrumbService.getBreadcrumbs$());
  customBreadcrumbComponent = toSignal(this.breadCrumbService.getBreadcrumbCustomComponent$());
  customBreadcrumbButtons = toSignal(this.breadCrumbService.getBreadcrumbCustomButtons$());
  breadcrumbsToRender = computed(() => {
    const breadcrumbs = this.breadcrumbs();
    // If there's a custom component, exclude the last breadcrumb since it will be replaced by the custom component
    if (this.customBreadcrumbComponent() != null && breadcrumbs && breadcrumbs.length > 0) {
      return breadcrumbs.slice(0, -1);
    }
    return breadcrumbs;
  });

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly breadCrumbService: BreadcrumbService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.registerRouterChange();
  }

  private registerRouterChange(): void {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        untilDestroyed(this),
        startWith(true) // trigger initial render
      )
      .subscribe(() => {
        this.breadCrumbService.setBreadcrumbs(this.getBreadcrumbs(this.activatedRoute.root));
        this.cdr.markForCheck();
      });
  }

  private getBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: BreadcrumbOption[] = []): BreadcrumbOption[] {
    const children: ActivatedRoute[] = route.children;

    // If there's no sub root, then stop the recurse and returns the generated breadcrumbs.
    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      if (child.outlet === PRIMARY_OUTLET) {
        // Only parse components in primary router-outlet (in another word, router-outlet without a specific name).
        // Parse this layer and generate a breadcrumb item.
        const routeUrl: string = child.snapshot.url
          .map((segment) => segment.path)
          .filter((path) => path)
          .join('/');

        // Do not change nextUrl if routeUrl is falsy. This happens when it's a route lazy loading other modules.
        const nextUrl = routeUrl ? `${url}/${routeUrl}` : url;
        const breadcrumbLabel = this.routeLabelFn(child.snapshot.data[this.routeLabel]);

        // If have data, go to generate a breadcrumb for it.
        if (routeUrl && breadcrumbLabel) {
          const breadcrumb: BreadcrumbOption = {
            label: breadcrumbLabel,
            params: child.snapshot.params,
            url: nextUrl,
          };
          breadcrumbs.push(breadcrumb);
        }
        return this.getBreadcrumbs(child, nextUrl, breadcrumbs);
      }
    }
    return breadcrumbs;
  }
}
